import { useMergeRefs } from '@floating-ui/react'
import classNames from 'classnames'

import { EMPTY_OBJECT } from '../../constants/common'
import { useFloatingTooltip } from '../../hooks'
import { getQaSelectorButton } from '../../utils/common'
import { FloatingTooltip, FloatingTooltipContent } from '../Floating'
import { ICON_COLOR, ICON_HEIGHT_16, IconSpinnerOverlay } from '../Icon'

import {
  BUTTON_CLASS_ALIASES,
  BUTTON_HEIGHT_24,
  BUTTON_HEIGHT_40,
  BUTTON_INLINE_CLASS_ALIASES,
  BUTTON_SQUARE_CLASS_ALIASES,
  ButtonType,
} from './Button.constants'
import { hasButtonIcon, isButtonSubmitType } from './Button.utils'
import { ButtonIcon } from './ButtonIcon'

import * as styles from './Button.module.css'

/**
 * IMPORTANT:
 * `interactive` controls visual and behavioral interactivity only.
 *
 * - `disabled` = button is unavailable (opacity, not-allowed cursor)
 * - `interactive=false` = button looks enabled but is non-interactive
 *
 * Do not mix responsibilities:
 * - Use `disabled` for real disabled states
 * - Use `interactive=false` to temporarily block interaction
 */
function Button({
  Element = 'button',
  afterIconProps: { height: afterIconHeight, ...afterIconProps } = EMPTY_OBJECT,
  beforeIconProps: {
    height: beforeIconHeight,
    ...beforeIconProps
  } = EMPTY_OBJECT,
  children,
  className,
  dataProps,
  disabled,
  elementRef,
  height = BUTTON_HEIGHT_40,
  interactive = true,
  isBorderless,
  isCircle,
  isInline,
  isLoading,
  isSkeleton,
  qaName,
  shadowed,
  showTextTooltip,
  style,
  tabIndex,
  text,
  textClassName,
  tooltip,
  tooltipClassName,
  tooltipDismissParams,
  tooltipPlacement,
  type,
  onClick,
  onKeyDown,
  onKeyUp,
  onMouseDown,
  onMouseLeave,
  onMouseUp,
}) {
  const { arrowRef, context, floatingStyles, getReferenceProps, isOpen, refs } =
    useFloatingTooltip({
      dismissParams: tooltipDismissParams,
      placement: tooltipPlacement,
    })

  const mergedRef = useMergeRefs([refs.setReference, elementRef])

  const hasBeforeIcon = hasButtonIcon(beforeIconProps)
  const hasAfterIcon = hasButtonIcon(afterIconProps)

  const isSquare = !text && !children && hasBeforeIcon

  const withIcon = (text || children) && (hasBeforeIcon || hasAfterIcon)

  const preparedTextClassName = classNames(styles.text, textClassName)

  const iconHeight = height <= BUTTON_HEIGHT_24 ? ICON_HEIGHT_16 : undefined

  const contentElement =
    isLoading && !hasBeforeIcon && !hasAfterIcon ? (
      <IconSpinnerOverlay
        color={ICON_COLOR.CurrentColor}
        size={ICON_HEIGHT_16}
      />
    ) : (
      <>
        {text ? (
          <FloatingTooltip content={text} disabled={!showTextTooltip}>
            <span className={preparedTextClassName}>{text}</span>
          </FloatingTooltip>
        ) : null}
        {children}
      </>
    )

  const buttonClassName = classNames(
    styles.button,
    {
      [styles.block]: !isInline && !isSquare && !isBorderless,
      [styles.circle]: isCircle,
      [styles.inert]: !interactive && !disabled,
      [styles.inline]: isInline && !isSquare,
      [styles.shadowed]: shadowed && !isSkeleton,
      [styles.skeleton]: isSkeleton,
      [styles.withIcon]: withIcon,
      [styles[BUTTON_CLASS_ALIASES[height]]]: !isInline && !isSquare,
      [styles[BUTTON_INLINE_CLASS_ALIASES[height]]]: isInline && !isSquare,
      [styles[BUTTON_SQUARE_CLASS_ALIASES[height]]]: isSquare,
    },
    className
  )

  const buttonProps = {
    ...(qaName ? getQaSelectorButton({ value: qaName }) : null),
  }

  return (
    <>
      <Element
        {...buttonProps}
        {...dataProps}
        {...getReferenceProps({
          onClick,
          onKeyDown,
          onMouseDown,
        })}
        className={buttonClassName}
        disabled={disabled || isLoading || isSkeleton || !interactive}
        ref={mergedRef}
        style={style}
        tabIndex={tabIndex}
        type={isButtonSubmitType(type) ? ButtonType.Submit : ButtonType.Button}
        onKeyUp={onKeyUp}
        onMouseLeave={onMouseLeave}
        onMouseUp={onMouseUp}
      >
        {hasBeforeIcon ? (
          <ButtonIcon
            {...beforeIconProps}
            height={beforeIconHeight || iconHeight}
            isLoading={isLoading}
          />
        ) : null}
        {contentElement}
        {hasAfterIcon ? (
          <ButtonIcon
            {...afterIconProps}
            height={afterIconHeight || iconHeight}
            isLoading={isLoading}
          />
        ) : null}
      </Element>
      {tooltip ? (
        <FloatingTooltipContent
          arrowRef={arrowRef}
          className={tooltipClassName}
          content={tooltip}
          context={context}
          floatingStyles={floatingStyles}
          isOpen={isOpen}
          refs={refs}
        />
      ) : null}
    </>
  )
}

export default Button
